/**
 * Query Explanation Panel
 *
 * Displays a collapsible explanation of how the AI processed a query,
 * showing tool calls, filters applied, and results.
 */

import React, { useState } from 'react';
import type { QueryExplanation, ToolStepExplanation, FilterExplanation } from './useAiSearchStream';
import { useTheme } from "@mui/material/styles";

interface QueryExplanationPanelProps {
  explanation: QueryExplanation;
}

/**
 * Format duration in human-readable format
 */
function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Render a single filter as a chip
 */
function FilterChip({ filter }: { filter: FilterExplanation }) {
  const displayValue = filter.valueLabel || filter.value;
  const relationModeLabel = filter.relationMode
    ? ` (${filter.relationMode === 'some' ? 'al menos uno' : filter.relationMode === 'every' ? 'todos' : 'ninguno'})`
    : '';

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        margin: '2px',
        backgroundColor: 'action.hover',
        borderRadius: '12px',
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontWeight: 500 }}>{filter.fieldLabel}</span>
      <span style={{ margin: '0 4px', opacity: 0.7 }}>{filter.operatorLabel}</span>
      <span style={{ fontStyle: 'italic' }}>{displayValue}</span>
      {relationModeLabel && (
        <span style={{ opacity: 0.6, marginLeft: '4px' }}>{relationModeLabel}</span>
      )}
    </span>
  );
}

/**
 * Render a single step in the explanation
 */
function StepItem({ step, index }: { step: ToolStepExplanation; index: number }) {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        padding: '8px 0',
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <div
        style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 600,
          flexShrink: 0,
          marginRight: '12px',
        }}
      >
        {index + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, marginBottom: '4px' }}>{step.action}</div>

        {step.filters && step.filters.length > 0 && (
          <div style={{ marginBottom: '4px' }}>
            {step.filters.map((filter, i) => (
              <FilterChip key={i} filter={filter} />
            ))}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.75rem',
            color: 'text.secondary',
          }}
        >
          <span>{step.result}</span>
          <span style={{ opacity: 0.7 }}>{formatDuration(step.durationMs)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Main panel component
 */
export function QueryExplanationPanel({ explanation }: QueryExplanationPanelProps) {
  const [expanded, setExpanded] = useState(false);

  if (!explanation || explanation.steps.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        marginTop: '8px',
        backgroundColor: 'background.default',
        borderRadius: '8px',
        overflow: 'hidden',
        fontSize: '0.875rem',
      }}
    >
      {/* Summary header - always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
        }}
      >
        <span
          style={{
            marginRight: '8px',
            fontSize: '1rem',
          }}
        >
          {'\u2139\uFE0F'}
        </span>
        <span style={{ flex: 1, color: 'text.secondary' }}>
          {explanation.summary}
        </span>
        <span
          style={{
            marginLeft: '8px',
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          {'\u25BC'}
        </span>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div style={{ padding: '0 12px 12px 12px' }}>
          {explanation.steps.map((step, index) => (
            <StepItem key={index} step={step} index={index} />
          ))}

          {/* Footer: Total time and tokens */}
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            {/* Token usage */}
            {explanation.tokenUsage && (
              <span style={{ opacity: 0.8 }}>
                {explanation.tokenUsage.promptTokens.toLocaleString()} in / {explanation.tokenUsage.completionTokens.toLocaleString()} out
              </span>
            )}
            {!explanation.tokenUsage && <span />}
            {/* Total time */}
            <span>Total: {formatDuration(explanation.totalDurationMs)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueryExplanationPanel;
